import {useId} from 'react'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import type {Icon} from '@primer/octicons-react'
import {Image, MinimalVideoPlayer, RiverBreakoutTabs, Text} from '@primer/react-brand/esm'
import {CommandActionMenu} from '../../_components/CommandActionMenu/CommandActionMenu'
import {LogoActionMenu} from '../../_components/LogoActionMenu/LogoActionMenu'
import {HeroCarouselBackground} from './HeroCarouselBackground'

import {
  findByIdWithSchema,
  findAllByContentType,
  findByContentType,
  GenericContentSchema,
  GenericGroupSchema,
  GenericAssetSchema,
  type GenericGroup,
} from '../../../../../brand/lib/types/contentful'
import {useGenericCtaAction} from '../../../../../brand/components/GenericCtaAction/use-generic-cta-action'

import styles from './HeroSection.module.css'

type Props = {
  component: GenericGroup
  heading?: string
}

export function HeroCarousel({component, heading}: Props) {
  const items = component.fields.content

  // App
  const appCarousel = findByIdWithSchema(items, 'appCarousel', GenericContentSchema)
  const appCarouselContent = appCarousel?.fields.content
  const appCtaContent = findByContentType(appCarouselContent, 'downloadOptions')
  const appCta = useGenericCtaAction(appCtaContent, {
    analytics: {tag: 'button', context: 'CTAs', location: 'hero'},
  })

  // CLI
  const cliCarousel = findByIdWithSchema(items, 'cliCarousel', GenericContentSchema)
  const cliCarouselContent = cliCarousel?.fields.content
  const cliCta = findByIdWithSchema(cliCarouselContent, 'cliCta', GenericGroupSchema)

  // IDE
  const ideCarousel = findByIdWithSchema(items, 'ideCarousel', GenericContentSchema)
  const ideCarouselContent = ideCarousel?.fields.content
  const ideCta = findByIdWithSchema(ideCarouselContent, 'ideCta', GenericGroupSchema)

  // Background
  const background = findByIdWithSchema(items, 'carouselBackground', GenericAssetSchema)

  // Slides
  const slides = findAllByContentType(items, 'genericContent')

  // eslint-disable-next-line @eslint-react/naming-convention-id-name
  const descriptionIdPrefix = useId()
  const accessibleHeading = heading ?? slides.at(0)?.fields.heading
  if (!accessibleHeading || slides.length === 0) return null

  return (
    <>
      <RiverBreakoutTabs
        backgroundVisual={background ? <HeroCarouselBackground component={background} /> : null}
        imagePosition="block-end"
        backgroundVisualFullBleed
      >
        <RiverBreakoutTabs.A11yHeading as="h2">{accessibleHeading}</RiverBreakoutTabs.A11yHeading>

        {slides.map(item => {
          const {label, heading: itemHeading, media} = item.fields
          const icon = getPrimerIcon(label?.fields.icon) as Icon
          // const link = getGenericContentLink(item)
          const mediaItem = media?.at(0)?.fields
          const mediaFile = mediaItem?.asset.fields.file
          const imageDimensions = mediaFile?.details?.image
          const description = mediaItem?.description ?? mediaItem?.asset.fields.description
          const descriptionId = description ? `${descriptionIdPrefix}-${item.sys.id}` : undefined

          return (
            <RiverBreakoutTabs.Item key={item.sys.id} className={styles.RiverBreakoutTabsItem}>
              {icon ? <RiverBreakoutTabs.Icon icon={icon} /> : null}
              {itemHeading ? <RiverBreakoutTabs.Heading>{itemHeading}</RiverBreakoutTabs.Heading> : null}

              <RiverBreakoutTabs.Content>
                {/*{link ? (
                  <Link
                    href={link.fields.href}
                    {...getAnalyticsEvent({
                      action: link.fields.text,
                      tag: 'button',
                      context: 'CTAs',
                      location: 'hero carousel',
                    })}
                  >
                    {link.fields.text}
                  </Link>
                ) : null}*/}
              </RiverBreakoutTabs.Content>

              {mediaFile ? (
                <RiverBreakoutTabs.Visual>
                  {imageDimensions ? (
                    <Image
                      src={mediaFile.url}
                      width={mediaItem?.width ?? imageDimensions.width}
                      height={mediaItem?.height ?? imageDimensions.height}
                      alt={description ?? ''}
                    />
                  ) : (
                    <>
                      {description ? (
                        <Text className="sr-only" id={descriptionId}>
                          {description}
                        </Text>
                      ) : null}
                      <MinimalVideoPlayer
                        width={mediaItem?.width}
                        height={mediaItem?.height}
                        title={itemHeading ?? t('Video')}
                        aria-describedby={descriptionId}
                      >
                        <source src={mediaFile.url} />
                      </MinimalVideoPlayer>
                    </>
                  )}
                </RiverBreakoutTabs.Visual>
              ) : null}
            </RiverBreakoutTabs.Item>
          )
        })}
      </RiverBreakoutTabs>

      {/* CTAs */}
      {/* Will be moved into RiverBreakoutTabs once support is added */}
      <div className={styles.actionsWrapper}>
        <div className={styles.actions}>
          {/* App */}
          <div>
            {appCta ? (
              <>
                {appCta.action}
                {appCta.feedback}
              </>
            ) : null}
          </div>

          {/* CLI */}
          <div>{cliCta ? <CommandActionMenu component={cliCta} location="hero" fullWidth /> : null}</div>

          {/* IDE */}
          <div>{ideCta ? <LogoActionMenu component={ideCta} location="hero" fullWidth /> : null}</div>
        </div>
      </div>
    </>
  )
}
